/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Small shared primitives. Deliberately unopinionated — the views own layout.
 */

import type { ReactNode } from "react";

import { Icon } from "./Icon.tsx";

export function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-80" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor"
        strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-ink-3" role="status">
      <Spinner /> {label}
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="card border-danger/30 bg-danger/5 p-4 text-sm" role="alert">
      <p className="text-danger">{message}</p>
      {onRetry && (
        <button className="btn-ghost mt-2" onClick={onRetry}>Try again</button>
      )}
    </div>
  );
}

/**
 * An empty state.
 *
 * ── WHY `action` IS REQUIRED ────────────────────────────────────────────────
 * It used to be optional, and all three call sites omitted it. "No recipes yet
 * · Add one, scan a page, or generate one" describes a way out and provides
 * none; the worst case sat inside the add-to-plan modal, where the user had to
 * dismiss the dialog and go find the Recipes tab themselves.
 *
 * An empty state is the first thing a new household sees on three screens. It
 * is onboarding, not an error message, so the type now insists there is a way
 * forward. Pass `action={null}` in the one legitimate case — a search that
 * matched nothing, where the way out is to edit the query already on screen.
 */
export function Empty({ title, hint, action }: {
  title: string;
  hint?: string;
  action: ReactNode;
}) {
  return (
    <div className="card p-8 text-center">
      <p className="type-section">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-sm text-sm text-ink-3">{hint}</p>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * Allergen badge.
 *
 * Three states, and the middle one matters: when an ingredient couldn't be
 * identified we do NOT know what's in the dish, so the badge says "may
 * contain" rather than implying safety. There is no green "safe" state —
 * the strongest claim the app makes is "no known conflicts".
 */
export function AllergenBadge({ tags, partial, conflictsWith }: {
  tags: string[]; partial: boolean; conflictsWith?: string[];
}) {
  if (conflictsWith?.length) {
    return (
      <span className="chip border-danger/40 bg-danger/10 text-danger">
        <Icon name="alert" className="h-3 w-3" />
        Contains {conflictsWith.join(", ")}
      </span>
    );
  }
  if (partial) {
    return (
      <span className="chip border-warn/40 bg-warn/10 text-warn" title="Some ingredients couldn't be identified">
        May contain — unverified
      </span>
    );
  }
  if (!tags.length) return null;
  return <span className="chip">{tags.join(", ")}</span>;
}

export function Banner({ tone = "info", children, onDismiss }: {
  tone?: "info" | "warn" | "danger"; children: ReactNode; onDismiss?: () => void;
}) {
  // Functional tones stay distinct from the olive brand accent — an allergen
  // warning must never read as brand chrome. Since the palette change that is
  // true by the numbers as well as by intent: 103° of hue between accent and
  // danger, where terracotta left only 17°.
  const cls = tone === "danger"
    ? "border-danger/40 bg-danger/10 text-danger"
    : tone === "warn"
      ? "border-warn/40 bg-warn/10 text-warn"
      : "border-accent/30 bg-accent-soft text-accent";
  return (
    <div className={`card flex items-start gap-3 p-3 text-sm ${cls}`} role="status">
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button className="shrink-0 opacity-60 transition hover:opacity-100"
                onClick={onDismiss} aria-label="Dismiss">
          <Icon name="x" className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: ReactNode; wide?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`card max-h-[90vh] w-full overflow-auto rounded-b-none sm:rounded-b-xl ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-rule-soft bg-paper-2 px-4 py-3">
          <h2 className="type-section">{title}</h2>
          <button className="-mr-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-ink-3 transition hover:bg-paper-3 hover:text-ink"
                  onClick={onClose} aria-label="Close">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ── Page chrome ─────────────────────────────────────────────────────────────
//
// Ported from MoveHelper, where every screen opens with a title, a one-line
// statement of what the screen is for, and an optional action slot. It reads as
// deliberate rather than as a list that happens to start at the top of the page.

export function PageHeader({ title, subtitle, actions }: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="type-display">{title}</h1>
        {subtitle && <p className="type-meta mt-1 max-w-prose">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const PILL_TONES: Record<string, string> = {
  neutral: "bg-paper-3 text-ink-2",
  accent: "bg-accent/15 text-accent",
  ok: "bg-ok/15 text-ok",
  warn: "bg-warn/20 text-ink-2",
  danger: "bg-danger/15 text-danger",
};

export function Pill({ tone = "neutral", children }: {
  tone?: keyof typeof PILL_TONES;
  children: ReactNode;
}) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PILL_TONES[tone]}`}>
      {children}
    </span>
  );
}
